import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../App.css";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { config } from "../config";
import { useNavigate } from "react-router-dom";

function SearchForm({ selectedDestination }) {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState("Return");
  const [formData, setFormData] = useState({
    origin: "",
    destination: selectedDestination || "",
    dateRange: [new Date(), new Date()],
    passengers: { adults: 1, children: 0, infants: 0, class: "Economy" },
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (selectedDestination) {
      setFormData((prev) => ({
        ...prev,
        destination: selectedDestination,
      }));
    }
  }, [selectedDestination]);

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setFormData({
      ...formData,
      dateRange: option === "Return" ? formData.dateRange : [new Date(), null],
    });
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setFormData({ ...formData, dateRange: [start, end] });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSearch = () => {
    const [outbound, returnDate] = formData.dateRange;

    // Format dates to YYYY-MM-DD
    const formatDate = (date) => {
      if (!date) return "";
      return date.toISOString().split("T")[0];
    };

    // Create the route parameter (e.g., "LHR-CDG")
    const route = `${formData.origin}-${formData.destination}`;

    // Build the query parameters
    const params = new URLSearchParams({
      outbound: formatDate(outbound),
      ...(returnDate && { return: formatDate(returnDate) }),
      adults: formData.passengers.adults,
      children: formData.passengers.children,
      infants: formData.passengers.infants,
      class: formData.passengers.class.charAt(0), // Y for Economy, etc.
      currency: "GBP",
    });

    // Navigate to the search results page
    navigate(`/flights/${route}?${params.toString()}`);
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const updatePassengers = (type, operation) => {
    setFormData((prevData) => {
      const newCount =
        operation === "increment"
          ? prevData.passengers[type] + 1
          : Math.max(prevData.passengers[type] - 1, 0);
      return {
        ...prevData,
        passengers: { ...prevData.passengers, [type]: newCount },
      };
    });
  };

  const updateClass = (e) => {
    setFormData({
      ...formData,
      passengers: { ...formData.passengers, class: e.target.value },
    });
  };

  const totalPassengers =
    formData.passengers.adults +
    formData.passengers.children +
    formData.passengers.infants;

  const passengerText = `${totalPassengers} Passengers, ${formData.passengers.class}`;

  return (
    <div className="flex items-center justify-center px-4">
      <div className="w-full max-w-4xl mx-auto bg-black bg-opacity-20 backdrop-blur-sm p-4 md:p-6 rounded-lg">
        <div className="flex justify-center mb-4">
          <div className="bg-black bg-opacity-30 rounded-full p-1">
            <button
              className={`px-4 py-2 text-sm md:text-base ${
                selectedOption === "Return"
                  ? `bg-white text-${config.primaryColor} rounded-full`
                  : "bg-transparent text-white"
              } rounded-l-full transition-colors`}
              onClick={() => handleOptionClick("Return")}
            >
              Return
            </button>
            <button
              className={`px-4 py-2 text-sm md:text-base ${
                selectedOption === "One way"
                  ? `bg-white text-${config.primaryColor} rounded-full`
                  : "bg-transparent text-white"
              } transition-colors`}
              onClick={() => handleOptionClick("One way")}
            >
              One way
            </button>
          </div>
        </div>
        <div className="bg-white w-full flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 p-3 md:p-5 rounded-3xl">
          <input
            type="text"
            name="origin"
            placeholder="Where from?"
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            onChange={handleInputChange}
          />
          <input
            type="text"
            name="destination"
            placeholder="Where to?"
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            value={formData.destination}
            onChange={handleInputChange}
          />
          <DatePicker
            selected={formData.dateRange[0]}
            onChange={handleDateChange}
            startDate={formData.dateRange[0]}
            endDate={formData.dateRange[1]}
            selectsRange={selectedOption === "Return"}
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 w-full"
            placeholderText={selectedOption === "Return" ? "Dates" : "Date"}
          />
          <button
            className="flex-1 p-3 rounded-xl md:rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 truncate overflow-hidden whitespace-nowrap text-left"
            onClick={toggleModal}
          >
            {passengerText}
          </button>
          <button
            className={`h-12 w-full md:w-12 bg-${config.primaryColor} text-white rounded-xl md:rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors`}
            onClick={handleSearch}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-md p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-left mb-6">Passengers</h3>
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Adults</span> (12+)
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("adults", "decrement")}
                >
                  -
                </button>
                <span className="px-2">{formData.passengers.adults}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("adults", "increment")}
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Children</span> (2-11)
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("children", "decrement")}
                >
                  -
                </button>
                <span className="px-2">{formData.passengers.children}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("children", "increment")}
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <p className="font-light text-gray-500">
                <span className="font-bold text-black">Infants</span> (0-1)
              </p>
              <div className="flex items-center">
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("infants", "decrement")}
                >
                  -
                </button>
                <span className="px-2">{formData.passengers.infants}</span>
                <button
                  className="px-2 rounded-full border"
                  onClick={() => updatePassengers("infants", "increment")}
                >
                  +
                </button>
              </div>
            </div>
            <div className="mt-4">
              <select
                value={formData.passengers.class}
                onChange={updateClass}
                className="w-full p-2 border rounded"
              >
                <option value="Economy">Economy</option>
                <option value="Premium Economy">Premium Economy</option>
                <option value="Business Class">Business Class</option>
                <option value="First Class">First Class</option>
              </select>
            </div>
            <button
              className="mt-4 text-blue-700 underline px-4 py-2 rounded"
              onClick={toggleModal}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchForm;
